/**
 * §P.5 · Los sistemas de grados del club.
 *
 * ## Se clona una plantilla, no se empieza de cero
 *
 * Nadie teclea los diez cinturones del taekwondo desde una pantalla en blanco:
 * existen plantillas globales —las comparten los doscientos clubes— y lo que hace
 * el club es clonar la suya y ajustarla. Por eso el vacío no dice «crea un sistema»
 * sino «elige el tuyo».
 *
 * `academy_id: null` es lo que distingue una plantilla global de un sistema propio,
 * **y es el campo que decide si salen los botones de editar**: tocar una plantilla
 * global cambiaría el sistema de los otros ciento noventa y nueve clubes.
 *
 * ## Clonar adopta, por defecto
 *
 * Clonar sin adoptar deja dos sistemas y ninguno en uso, que es un estado en el que
 * los exámenes no saben de qué escalera hablan. El contrato lo pone a `true` y aquí
 * no se ofrece lo contrario: quien clona su sistema es porque va a usarlo.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { Cinturon } from '@/club/grados/Cinturon';
import {
  nombreDeCinturon,
  type Cinturon as TipoDeCinturon,
  type SistemaDeGrados,
} from '@/club/grados/tipos';

export function SistemasDeGrados({
  club,
  puedeEditar,
  alEditarEscalera,
}: {
  club: string;
  puedeEditar: boolean;
  alEditarEscalera: (schemeId: string) => void;
}) {
  const cache = useQueryClient();
  const [clonando, setClonando] = useState(false);
  const [problema, setProblema] = useState<Problema | null>(null);

  const sistemas = useQuery({
    queryKey: ['grados', club, clonando],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club })['belt-schemes'].get({
        /*
         * Siempre con las plantillas, y se filtran abajo. Antes solo al clonar, y
         * un club que usa la plantilla tal cual —Ajustes → Disciplinas lo ofrece
         * así— leía aquí «Todavía no has elegido tu sistema de grados» teniéndolo
         * elegido. 24-09-2026.
         */
        query: { include_templates: true },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as SistemaDeGrados[];
    },
  });

  const clonar = useMutation({
    mutationFn: async (plantilla: SistemaDeGrados) => {
      const r = await api.v1.clubs({ club })['belt-schemes'].post({
        template_id: plantilla.id,
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as SistemaDeGrados;
    },
    onSuccess: () => {
      setClonando(false);
      void cache.invalidateQueries({ queryKey: ['grados', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (sistemas.isPending) {
    return <Cargando filas={4} etiqueta="Cargando los grados" />;
  }

  if (sistemas.isError || !sistemas.data) {
    return (
      <PanelError
        problema={aProblema(sistemas.error)}
        reintentar={() => void sistemas.refetch()}
      />
    );
  }

  // Los del club: los que ha copiado y la plantilla que usa tal cual. Las demás
  // plantillas solo salen al elegir una.
  const propios = sistemas.data.filter(
    (s) => s.academy_id !== null || s.in_use
  );
  const plantillas = sistemas.data.filter((s) => s.academy_id === null);

  // ── Elegir una plantilla ─────────────────────────────────────────────────
  if (clonando) {
    return (
      <section>
        <div className="titulo-fila">
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setClonando(false);
              setProblema(null);
            }}
          >
            ← Grados
          </button>
        </div>

        <h1>Elige el sistema de grados</h1>

        <p className="tenue">
          Se copia a tu club y a partir de ahí es tuyo: puedes cambiar los
          nombres, los colores y los requisitos sin que afecte a nadie más.
        </p>

        {problema && <PanelError problema={problema} />}

        {plantillas.length === 0 ? (
          <Vacio
            titulo="No hay plantillas para tus disciplinas"
            explicacion="Las plantillas van por disciplina, y el club tiene que tener la suya dada de alta. Se configura en los datos del club."
          />
        ) : (
          <ul className="plantillas">
            {plantillas.map((p) => (
              <li key={p.id}>
                <div>
                  <strong>{p.name}</strong>
                  <div className="tenue">
                    {p.discipline_name} · {p.rank_count} grados
                    {p.federation ? ` · ${p.federation}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={clonar.isPending}
                  onClick={() => clonar.mutate(p)}
                >
                  {clonar.isPending ? 'Copiando…' : 'Usar este'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    );
  }

  // ── Los sistemas del club ────────────────────────────────────────────────
  return (
    <section>
      <header className="titulo-fila">
        <h1>Grados</h1>
        {puedeEditar && propios.length > 0 && (
          <button type="button" onClick={() => setClonando(true)}>
            Añadir otro sistema
          </button>
        )}
      </header>

      <p className="tenue">
        La escalera de cinturones de cada disciplina. De aquí salen los exámenes
        y el pasaporte de cada alumno.
      </p>

      {problema && <PanelError problema={problema} />}

      {propios.length === 0 ? (
        <Vacio
          titulo="Todavía no has elegido tu sistema de grados"
          explicacion="No hay que teclear los cinturones uno a uno: se copia el sistema estándar de tu disciplina y se ajusta lo que haga falta."
          {...(puedeEditar
            ? {
                accion: (
                  <button type="button" onClick={() => setClonando(true)}>
                    Elegir el sistema
                  </button>
                ),
              }
            : {})}
        />
      ) : (
        <div className="sistemas">
          {propios.map((s) => (
            <SistemaConSusCinturones
              key={s.id}
              club={club}
              sistema={s}
              // La plantilla es de todos los clubes: se usa, no se edita. Para
              // cambiarla, se copia con «Añadir otro sistema».
              puedeEditar={puedeEditar && s.academy_id !== null}
              alEditar={() => alEditarEscalera(s.id)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Un sistema con su escalera desplegada.
 *
 * Los cinturones se piden aparte y se pintan siempre: **la escalera es lo que la
 * gente viene a ver**, y esconderla tras un «ver detalle» convierte la pantalla en
 * un índice de una cosa.
 */
function SistemaConSusCinturones({
  club,
  sistema,
  puedeEditar,
  alEditar,
}: {
  club: string;
  sistema: SistemaDeGrados;
  puedeEditar: boolean;
  alEditar: () => void;
}) {
  const cinturones = useQuery({
    queryKey: ['cinturones', club, sistema.id],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['belt-schemes']({ schemeId: sistema.id })
        .ranks.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as TipoDeCinturon[];
    },
  });

  return (
    <article className="sistema">
      <div className="titulo-fila">
        <h2 className="seccion">
          {sistema.name}
          {sistema.in_use ? (
            <span className="pastilla pastilla-bien pastilla-left">En uso</span>
          ) : (
            <span className="pastilla pastilla-left">Sin usar</span>
          )}
        </h2>
        {puedeEditar && (
          <button type="button" className="btn-plano" onClick={alEditar}>
            Editar la escalera
          </button>
        )}
      </div>

      <p className="tenue">
        {sistema.discipline_name} · {sistema.rank_count} grados
        {sistema.federation ? ` · ${sistema.federation}` : ''}
      </p>

      {cinturones.isPending && <Cargando filas={3} etiqueta="Cargando" />}

      {cinturones.isError && (
        <PanelError
          problema={aProblema(cinturones.error)}
          reintentar={() => void cinturones.refetch()}
        />
      )}

      {cinturones.data && (
        <ol className="escalera">
          {/* Del primero al último: es el orden en que se sube, no el alfabético. */}
          {cinturones.data.map((c) => (
            <li key={c.id}>
              <Cinturon
                color={c.color_hex}
                color2={c.color_hex_2}
                nombre={nombreDeCinturon(c)}
                deDan={c.is_dan}
              />
              <span className="tenue">
                {c.max_age !== null && `solo menores de ${c.max_age} · `}
                {c.students_with_it === 0
                  ? 'nadie'
                  : c.students_with_it === 1
                    ? '1 alumno'
                    : `${c.students_with_it} alumnos`}
              </span>
            </li>
          ))}
        </ol>
      )}
    </article>
  );
}
